export interface CheckoutConfig {
  enabled: boolean;
  applicationId?: string;
  locationId?: string;
  environment?: 'sandbox' | 'production';
  sdkUrl?: string;
  squareMode?: 'sandbox' | 'production';
  inventoryMode?: 'simulated' | 'live';
}

export interface CheckoutItem {
  sku: string;
  siteId: number;
}

export interface CheckoutPayResult {
  success: boolean;
  orderId: string;
  paymentId: string;
  orderNumber?: string;
  receiptToken?: string;
  itemsMarkedSold: number;
  inventoryMode?: 'simulated' | 'live';
  squareMode?: 'sandbox' | 'production';
  isTest?: boolean;
  orderPersisted?: boolean;
  subtotal?: number;
  discount?: number;
  shippingDiscount?: number;
  gst?: number;
  pst?: number;
  hst?: number;
  taxCents?: number;
  taxProvince?: string;
  total: number;
  currency: string;
  couponCode?: string | null;
}

export interface CustomerInfo {
  name: string;
  email: string;
  phone?: string;
  shippingMethod?: string;
}

const CHECKOUT_API_URL =
  import.meta.env.VITE_CHECKOUT_API_URL ||
  import.meta.env.VITE_API_URL?.replace('/api/v1', '') ||
  'http://localhost:8787';

export async function fetchCheckoutConfig(): Promise<CheckoutConfig> {
  const res = await fetch(`${CHECKOUT_API_URL}/config`);
  if (!res.ok) throw new Error(`Checkout config unavailable (${res.status})`);
  const json = await res.json();
  return json.data;
}

export async function processCheckoutPayment(input: {
  idempotencyKey: string;
  sourceId: string;
  postalCode?: string;
  items: CheckoutItem[];
  customer?: CustomerInfo;
  couponCode?: string;
  shippingCents?: number;
  customerId?: number | null;
  cartSessionId?: string | null;
}): Promise<CheckoutPayResult> {
  const res = await fetch(`${CHECKOUT_API_URL}/pay`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });

  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(json.message || json.error || `Payment failed (${res.status})`);
  }
  return json.data;
}

export async function checkAvailability(
  items: CheckoutItem[],
  cartSessionId?: string | null,
): Promise<Array<{ sku: string; siteId: number; available: boolean; reason?: string }>> {
  const res = await fetch(`${CHECKOUT_API_URL}/check-availability`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items, cartSessionId: cartSessionId || undefined }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || 'Availability check failed');
  return json.data;
}
